import React, { useMemo } from 'react';
import classNames from 'classnames';

import classes from '@nerdwallet/base-styles/classes';
import StructuredContent from '@nerdwallet/structured-content';
import {
  Accent,
  AuthorByline,
  CenteredContainer,
  Stack,
  Box,
  Columns,
  Column,
  AdvertiserDisclosure as NWAdvertiserDisclosure,
  StarRating,
  Text,
  Cluster,
} from '@nerdwallet/currency';

import ContentPageHeader from '~/client/components/content-page-header/content-page-header';
import EditorialStandards from '~/client/components/editorial-standards';
import ArticleStructuredContent from '~/client/components/article-structured-content/article-structured-content';
import {
  augmentWithToc,
  constructTableOfContentsDocument,
  constructMiniProductCard,
  TocSchema,
} from '~/lib/structured-content';

import AuthorBox from '~/client/components/author-box/author-box';
import Methodology from '~/client/components/methodology/methodology';
import ConnectedOutdatedPageNotification from '~/client/components/outdated-page-notification/outdated-page-notification';

import styles from './general-article-editorial-test.module.less';
import { ArticleTemplateProps } from '~/lib/types';
import { computeDisplayedDate } from '~/lib/article-utils';

const GeneralArticleEditorialTest: React.FC<ArticleTemplateProps> = ({
  hasTableOfContents,
  tableOfContentsHeading,
  graphqlArticle,
}) => {
  const { items: tocItems, augmentedDoc } = augmentWithToc(
    graphqlArticle.content
  );

  const miniProductCard = useMemo(() => {
    const cardId = '1fc99a80-1508-4e14-83d7-eb5d5da65d97';

    const productHighlights = Object.freeze([
      { title: 'Sign-up bonus', detail: '60k miles' },
      { title: 'Rewards rate', detail: '2x - 5x miles' },
      { title: 'No foreign transaction fees', detail: '' },
    ]);

    return constructMiniProductCard({
      align: 'center',
      id: cardId,
      useLargeWidth: true,
      productHighlights,
    });
  }, []);

  const nwRating = 4.4;
  const rightRailContents = (
    <React.Fragment>
      <Box marginBottom="4" className={classes(['text-align-right'])}>
        <NWAdvertiserDisclosure type="credit_cards" />
      </Box>

      <Box backgroundColor="neutral-lightest">
        <Stack spacing="3">
          <Box
            alignItems="center"
            paddingTop="3"
            className={classes(['text-align-center'])}
          >
            <Text bold size="default">
              Nerdwallet featured on:
            </Text>
          </Box>
          <Box alignContent="center" paddingBottom="3">
            <Cluster spacing="0" align="center">
              <img
                alt=""
                width="60"
                src="https://www.nerdwallet.com/cdn/img/landing/logos/buzzfeed_280x112.png"
              />
              <img
                alt=""
                width="60"
                src="https://www.nerdwallet.com/cdn/img/landing/logos/lifehacker_280x112.png"
              />
              <img
                alt=""
                width="60"
                src="https://www.nerdwallet.com/cdn/img/landing/logos/forbes_280x112.png"
              />
              <img
                alt=""
                width="60"
                src="https://www.nerdwallet.com/cdn/img/landing/logos/time_280x112.png"
              />
              <img
                alt=""
                width="60"
                src="https://www.nerdwallet.com/cdn/img/landing/logos/usa_today_280x112.png"
              />
            </Cluster>
          </Box>
        </Stack>
      </Box>

      <Box
        marginY="3"
        paddingY="3"
        borderTop="1"
        borderBottom="1"
        borderColor="neutral-light"
        className={classes(['text-align-center'])}
      >
        <Text light size="small">
          Nerdwallet has gotten
        </Text>
        <Box paddingY="1">
          <Text bold size="large">
            {nwRating} <StarRating nerdwallet rating={nwRating} />
          </Text>
        </Box>
        <Text light size="default">
          Overall client satisfaction according to Shopper Approved
        </Text>
      </Box>

      <Box
        border="1"
        borderRadius="2"
        borderColor="neutral-light"
        className={classes(['text-align-center'])}
      >
        <Box
          paddingY="1"
          backgroundColor="blue-lighter"
          className={classes(['text-align-center'])}
        >
          <h6>FEATURED PICK</h6>
        </Box>
        <Box
          paddingTop="3"
          paddingBottom="2"
          className={classes(['text-align-center'])}
        >
          <StructuredContent>{miniProductCard}</StructuredContent>
        </Box>
      </Box>
    </React.Fragment>
  );

  return (
    <CenteredContainer>
      <Stack spacing={4}>
        <ContentPageHeader
          article={graphqlArticle}
          category={null}
          width={{ desktop: 8, mobile: 12 }}
          imageHasFullWidth
          hideEditorialStandards
          showAdvertiserDisclosure={false}
        />
        <Columns>
          <Column width={{ desktop: 8, mobile: 12 }}>
            <Stack spacing={4}>
              {graphqlArticle.heroBannerDocument &&
                graphqlArticle.showAuthorByline && (
                  <AuthorByline
                    authoringDate={computeDisplayedDate({
                      displayDate: graphqlArticle.dateTimeDisplayDate,
                      showLastModifiedDate: graphqlArticle.showLastModifiedDate,
                      hideDate: graphqlArticle.hideDate,
                      marketplaceEntities: null,
                    })}
                    authors={graphqlArticle.authors.map((author) => ({
                      name: author.name,
                      url: author.link,
                    }))}
                  />
                )}
              {graphqlArticle.showEditorialDisclaimer && (
                <EditorialStandards article={graphqlArticle} />
              )}
              <ConnectedOutdatedPageNotification />
              {hasTableOfContents && tocItems.length && (
                <div className={classNames(styles['toc-wide-variant'])}>
                  <Accent size="small" className={classes('margin-bottom-3')}>
                    {tableOfContentsHeading}
                  </Accent>
                  <div className={styles.hideNotDesktop}>
                    <StructuredContent>
                      {constructTableOfContentsDocument({
                        items: tocItems,
                        hasJumpCTA: true,
                        showMore: true,
                      })}
                    </StructuredContent>
                  </div>
                  <div className={styles.hideDesktop}>
                    <StructuredContent>
                      {constructTableOfContentsDocument({
                        items: tocItems,
                        hasJumpCTA: true,
                      })}
                    </StructuredContent>
                  </div>
                </div>
              )}
              <main>
                <div className={classes('margin-bottom-4')}>
                  {hasTableOfContents ? (
                    <ArticleStructuredContent schema={TocSchema}>
                      {augmentedDoc}
                    </ArticleStructuredContent>
                  ) : (
                    <ArticleStructuredContent>
                      {graphqlArticle.content}
                    </ArticleStructuredContent>
                  )}
                </div>
                <AuthorBox article={graphqlArticle} />
                <Methodology article={graphqlArticle} />
              </main>
              <Box
                display={{ desktop: 'none', mobile: 'flex' }}
                align-items="flex-start"
                marginBottom="4"
              >
                <Box position="sticky" className={styles.stickyBox}>
                  {rightRailContents}
                </Box>
              </Box>
            </Stack>
          </Column>
          <Column width={{ desktop: 4 }}>
            <Box
              display={{ desktop: 'flex', mobile: 'none' }}
              height="100"
              paddingLeft="3-half"
              align-items="flex-start"
            >
              <Box position="sticky" className={styles.stickyBox}>
                {rightRailContents}
              </Box>
            </Box>
          </Column>
        </Columns>
      </Stack>
    </CenteredContainer>
  );
};

export default GeneralArticleEditorialTest;
